import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EnvelopeIcon, BuildingOfficeIcon, UserIcon, BriefcaseIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { getPlanningDeviceId } from '@/services/planningService';
import { useAuth } from '@/state/auth/AuthProvider';
import { PublicHeader } from '@/components/public/PublicHeader';
import { useLanguage } from '@/state/language/LanguageProvider';

interface RegistrationStep {
  step: 'email' | 'plan' | 'purpose' | 'details' | 'complete';
  data: any;
}

export const EnhancedRegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const invitationCode = searchParams.get('code');
  const token = searchParams.get('token');
  // Prefill from landing CTA: /register?email=...
  const emailFromQuery = (searchParams.get('email') || '').trim();
  
  const [currentStep, setCurrentStep] = useState<RegistrationStep['step']>(
    // Paid plans are chosen after login; public signup skips plan step (Free only).
    invitationCode ? 'details' : token ? 'purpose' : 'email'
  );
  
  const [showInlineLogin, setShowInlineLogin] = useState(false);
  const [inlinePassword, setInlinePassword] = useState('');
  
  const [formData, setFormData] = useState({
    email: emailFromQuery,
    invitationCode: invitationCode || '',
    purpose: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    phone: '',
    organizationName: '',
    organizationDetails: {}
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [emailSent, setEmailSent] = useState(false);
  // Free-only public signup — no paid plan selection
  const selectedPlanId = '';

  const brandBg = 'bg-[#1a5d2e]';
  const brandBgHover = 'hover:bg-[#0d5d2c]';
  const brandText = 'text-[#1a5d2e]';
  const brandRing = 'focus:ring-[#1a5d2e] focus:border-[#1a5d2e]';

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePurposeSelection = (purpose: string) => {
    setFormData(prev => ({ ...prev, purpose }));
    setCurrentStep('details');
  };

  const validateInvitation = async () => {
    if (!formData.invitationCode) return;
    
    try {
      const response = await api.post('/auth/register/validate-invitation', {
        invitationCode: formData.invitationCode
      });
      
      if (response.data.success) {
        setInvitationData(response.data.data);
        setFormData(prev => ({ 
          ...prev, 
          purpose: response.data.data.tenantType.toLowerCase(),
          organizationName: response.data.data.tenantName
        }));
        setCurrentStep('details');
      }
    } catch (error) {
      console.error('Invitation validation error:', error);
      toast.error(t('auth.register.invalidInvitationCode'));
    }
  };

  const handleEmailSubmit = async () => {
    if (!formData.email) {
      toast.error(t('auth.emailRequired'));
      return;
    }

    setIsLoading(true);
    setShowInlineLogin(false);

    try {
      const checkRes = await api.post<{ success: boolean; exists: boolean }>('/auth/register/check-email', {
        email: formData.email.trim(),
        billingPlanId: selectedPlanId || undefined,
      });
      if (!checkRes.data.success) {
        toast.error(t('auth.register.somethingWentWrong'));
        return;
      }
      if (checkRes.data.exists) {
        setShowInlineLogin(true);
        return;
      }

      const response = await api.post('/auth/register/initiate', {
        email: formData.email.trim(),
        invitationCode: formData.invitationCode || undefined,
        billingPlanId: selectedPlanId || undefined,
      });

      if (response.data.success) {
        if (formData.invitationCode) {
          await validateInvitation();
        } else {
          setEmailSent(true);
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('auth.failedToSendConfirmationEmail');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /** Fallback if plan step is reached — Free only; paid plans are post-login. */
  const renderPlanStep = () => (
    <div className="max-w-[600px] mx-auto">
      <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">
        {t('auth.register.startFree')}
      </h1>
      <p className="text-sm text-gray-500 mb-4">{t('auth.register.startFreeSubtitle')}</p>
      <p className={`text-xs ${brandText} mb-6`}>
        {t('auth.register.currentSelection')}:{' '}
        <span className="font-medium">{t('auth.register.freeNoPlanYet')}</span>
      </p>
      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrentStep('email')}
          className="text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => setCurrentStep('purpose')}
          className={`px-4 py-2.5 rounded-md ${brandBg} text-white text-sm font-medium ${brandBgHover}`}
        >
          {t('common.continue')}
        </button>
      </div>
    </div>
  );

  const handleInlineLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !inlinePassword) {
      toast.error(t('auth.register.emailAndPasswordRequired'));
      return;
    }
    setIsLoading(true);
    try {
      const authResponse = await login({ email: formData.email.trim(), password: inlinePassword });
      toast.success(t('auth.loginSuccessful'));
      if (authResponse?.requirePasswordChange) {
        navigate('/change-password?required=true', { replace: true });
      } else if (authResponse?.planAttempt) {
        const params = new URLSearchParams({ section: 'subscription' });
        if (authResponse.planAttempt.billingPlanId) params.set('planId', authResponse.planAttempt.billingPlanId);
        params.set('attemptId', authResponse.planAttempt.id);
        navigate(`/settings/profile?${params.toString()}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetailsSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.password) {
      toast.error(t('auth.register.requiredFieldsMissing'));
        return;
      }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/register/complete', {
        token: new URLSearchParams(window.location.search).get('token'),
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        phone: formData.phone,
        purpose: formData.purpose,
        organizationName: formData.organizationName,
        organizationDetails: formData.organizationDetails,
        billingPlanId: selectedPlanId || undefined,
      }, {
        headers: { 'X-Device-Id': getPlanningDeviceId() }
      });

      if (response.data.success) {
        const { email, planAttempt } = response.data.data || {};
        if (email && formData.password) {
          try {
            const authResponse = await login({ email, password: formData.password });
            toast.success('Account created. Welcome!');
            if (authResponse?.requirePasswordChange) {
              navigate('/change-password?required=true', { replace: true });
            } else if (authResponse?.planAttempt || planAttempt) {
              const pa = authResponse?.planAttempt ?? planAttempt;
              const params = new URLSearchParams({ section: 'subscription' });
              if (pa?.billingPlanId) params.set('planId', pa.billingPlanId);
              if (pa?.id) params.set('attemptId', pa.id);
              navigate(`/settings/profile?${params.toString()}`, { replace: true });
            } else {
              // No admin-provided subscription: send to subscription tab to add plan + card (requirement §5.5.3).
              navigate('/settings/profile?section=subscription', { replace: true });
            }
            return;
          } catch (loginErr: any) {
            console.warn('Auto-login after registration failed:', loginErr);
            toast.success('Account created. Please sign in.');
          }
        } else {
          toast.success('Account created successfully!');
        }
        setCurrentStep('complete');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('auth.registrationFailed');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderEmailStep = () => {
    if (emailSent && !invitationCode) {
      return (
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#e8f5ed] mb-5">
            <EnvelopeIcon className={`h-6 w-6 ${brandText}`} aria-hidden />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('auth.checkEmail')}
          </h2>
          <p className="text-gray-600 text-sm max-w-sm mx-auto mb-1">
            {t('auth.register.emailLinkSent', { email: formData.email })}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {t('auth.register.checkSpam')}
          </p>
          <Link
            to="/"
            className={`text-sm font-medium ${brandText} hover:text-[#0d5d2c]`}
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      );
    }

    if (showInlineLogin) {
      return (
        <div className="max-w-[360px] mx-auto">
          <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">
            {t('auth.signInToContinue')}
          </h1>
          <p className="text-sm text-gray-500 mb-4">
            {t('auth.register.emailAlreadyRegistered')}
          </p>
          <form onSubmit={handleInlineLogin} className="space-y-4">
            <div>
              <label htmlFor="inline-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.email')}
              </label>
              <input
                id="inline-email"
                type="email"
                value={formData.email}
                readOnly
                className="block w-full rounded-md border border-gray-200 bg-gray-50 py-2.5 px-3 text-gray-600 text-sm"
              />
            </div>
            <div>
              <label htmlFor="inline-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.password')}
              </label>
              <input
                id="inline-password"
                type="password"
                value={inlinePassword}
                onChange={(e) => setInlinePassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                className={`block w-full rounded-md border border-gray-300 py-2.5 px-3 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-1 ${brandRing} text-sm`}
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full rounded-md ${brandBg} py-2.5 px-3 text-sm font-medium text-white shadow-sm ${brandBgHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a5d2e] disabled:opacity-50 disabled:pointer-events-none`}
            >
              {isLoading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>
          <button
            type="button"
            onClick={() => { setShowInlineLogin(false); setInlinePassword(''); }}
            className="mt-4 w-full text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            {t('auth.register.useDifferentEmail')}
          </button>
        </div>
      );
    }

    return (
      <div className="max-w-[360px] mx-auto">
        <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">
          {invitationCode ? t('auth.register.completeRegistrationTitle') : t('auth.createAccount')}
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          {invitationCode
            ? t('auth.register.invitedEnterEmail')
            : t('auth.register.enterEmailWeWillSendLink')}
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); handleEmailSubmit(); }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.email')}
            </label>
            <input
              id="register-email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              className={`block w-full rounded-md border border-gray-300 py-2.5 px-3 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-1 ${brandRing} text-sm`}
              required
            />
          </div>

          {invitationCode && (
            <div>
              <label htmlFor="register-code" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.register.invitationCode')}
              </label>
              <input
                id="register-code"
                type="text"
                value={formData.invitationCode}
                onChange={(e) => handleInputChange('invitationCode', e.target.value)}
                placeholder={t('auth.register.invitationCodePlaceholder')}
                className={`block w-full rounded-md border border-gray-300 py-2.5 px-3 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-1 ${brandRing} text-sm`}
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full rounded-md ${brandBg} py-2.5 px-3 text-sm font-medium text-white shadow-sm ${brandBgHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a5d2e] disabled:opacity-50 disabled:pointer-events-none`}
          >
            {isLoading ? t('common.continue') : t('common.continue')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/" className={`font-medium ${brandText} hover:text-[#0d5d2c]`}>
            {t('auth.signIn')}
          </Link>
        </p>
      </div>
    );
  };

  const renderPurposeStep = () => (
    <div className="max-w-[480px] mx-auto">
      <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">
        {t('auth.register.howWillYouUse')}
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        {t('auth.register.chooseRoleSubtitle')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => handlePurposeSelection('INDIVIDUAL')}
          className={`flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-white text-left hover:border-gray-300 hover:bg-gray-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
            <UserIcon className="h-5 w-5" />
          </span>
          <span>
            <span className="block font-medium text-gray-900 text-sm">{t('auth.register.purpose.individual')}</span>
            <span className="block text-xs text-gray-500 mt-0.5">{t('auth.register.purpose.individualSubtitle')}</span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => handlePurposeSelection('TENANT')}
          className={`flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-white text-left hover:border-gray-300 hover:bg-gray-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
            <BuildingOfficeIcon className="h-5 w-5" />
          </span>
          <span>
            <span className="block font-medium text-gray-900 text-sm">{t('auth.register.purpose.company')}</span>
            <span className="block text-xs text-gray-500 mt-0.5">{t('auth.register.purpose.companySubtitle')}</span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => handlePurposeSelection('CONSULTANT')}
          className={`flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-white text-left hover:border-gray-300 hover:bg-gray-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
            <BriefcaseIcon className="h-5 w-5" />
          </span>
          <span>
            <span className="block font-medium text-gray-900 text-sm">{t('auth.register.purpose.consulting')}</span>
            <span className="block text-xs text-gray-500 mt-0.5">{t('auth.register.purpose.consultingSubtitle')}</span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => handlePurposeSelection('PORTFOLIO')}
          className={`flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-white text-left hover:border-gray-300 hover:bg-gray-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
            <ChartBarIcon className="h-5 w-5" />
          </span>
          <span>
            <span className="block font-medium text-gray-900 text-sm">{t('auth.register.purpose.portfolio')}</span>
            <span className="block text-xs text-gray-500 mt-0.5">{t('auth.register.purpose.portfolioSubtitle')}</span>
          </span>
        </button>
      </div>
    </div>
  );

  const inputClass =
    `block w-full rounded-md border border-gray-300 py-2.5 px-3 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-1 ${brandRing} text-sm`;

  const renderDetailsStep = () => (
    <div className="max-w-[400px] mx-auto">
      <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">
        {invitationData ? `Join ${invitationData.tenantName}` : 'Complete your profile'}
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        {invitationData ? t('auth.register.finishSetup') : t('auth.register.fewDetails')}
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); handleDetailsSubmit(); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="details-first" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('userManagement.firstName')}
            </label>
            <input
              id="details-first"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              autoComplete="given-name"
              className={inputClass}
              required
            />
          </div>
          <div>
            <label htmlFor="details-last" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('userManagement.lastName')}
            </label>
            <input
              id="details-last"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              autoComplete="family-name"
              className={inputClass}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="details-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('auth.phone')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
          </label>
          <input
            id="details-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            autoComplete="tel"
            className={inputClass}
          />
        </div>

        {formData.purpose !== 'INDIVIDUAL' && (
          <div>
            <label htmlFor="details-org" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.register.organizationName')}
            </label>
            <input
              id="details-org"
              type="text"
              value={formData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              autoComplete="organization"
              className={inputClass}
              required
            />
          </div>
        )}

        <div>
          <label htmlFor="details-password" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('auth.password')}
          </label>
          <input
            id="details-password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            autoComplete="new-password"
            className={inputClass}
            required
          />
          <p className="mt-1 text-xs text-gray-500">{t('auth.passwordRequirements')}</p>
        </div>

        <div>
          <label htmlFor="details-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('auth.confirmPassword')}
          </label>
          <input
            id="details-confirm"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            autoComplete="new-password"
            className={inputClass}
            required
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full rounded-md ${brandBg} py-2.5 px-3 text-sm font-medium text-white shadow-sm ${brandBgHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a5d2e] disabled:opacity-50 disabled:pointer-events-none mt-2`}
        >
          {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
        </button>
      </form>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="text-center max-w-sm mx-auto py-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#e8f5ed] mb-5">
        <svg className={`h-6 w-6 ${brandText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">
        {t('auth.register.allSet')}
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        {t('auth.register.accountCreatedSignIn')}
      </p>
      <Link
        to="/"
        className={`inline-flex items-center justify-center rounded-md ${brandBg} py-2.5 px-5 text-sm font-medium text-white shadow-sm ${brandBgHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a5d2e]`}
      >
        {t('auth.signIn')}
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-[440px]">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 py-8 px-6 sm:py-10 sm:px-8">
            {currentStep === 'email' && renderEmailStep()}
            {currentStep === 'plan' && renderPlanStep()}
            {currentStep === 'purpose' && renderPurposeStep()}
            {currentStep === 'details' && renderDetailsStep()}
            {currentStep === 'complete' && renderCompleteStep()}
          </div>
        </div>
      </main>

    </div>
  );
};